import { motion } from "framer-motion";
import { trackBetaApplication } from "@/lib/analytics";

const features = [
  "One customer cohort analyzed, up to 5,000 buyers",
  "Your rich customer avatar and highest-value segments",
  "Private customer intelligence dashboard",
  "Recorded 60-minute growth diagnosis with Nick",
  "Written 30 / 60 / 90-day marketing action plan",
  "Priorities across positioning, acquisition, retention, and creative",
  "AI-ready customer context your team can reuse",
  "One factual correction pass after delivery",
];

export default function Pricing() {
  return (
    <section id="pricing" data-testid="pricing-section" className="relative py-12 lg:py-20 border-t border-white/[0.06] scroll-mt-24">
      <div aria-hidden className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse 900px 500px at 50% 0%, rgba(212, 255, 74, 0.07) 0%, transparent 60%)" }} />
      <div className="relative max-w-[1200px] mx-auto px-6 lg:px-12">
        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-100px" }} transition={{ duration: 0.7 }} className="text-center mb-10 lg:mb-14">
          <div className="font-mono-label text-[#D4FF4A]/80 mb-4 flex items-center justify-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-[#D4FF4A] shadow-[0_0_10px_rgba(212,255,74,0.7)] animate-pulse" />
            Five-customer beta
          </div>
          <h2 data-testid="pricing-heading" className="font-display text-white text-[40px] lg:text-[64px] font-semibold leading-[1.04] tracking-[-0.03em] mb-4">
            Find your rich customer avatar.<br className="hidden sm:block" /> Make a lot more money.
          </h2>
          <p className="text-[#A1A1AA] text-lg max-w-[700px] mx-auto leading-[1.6]">
            One focused engagement that gives you the customer intelligence and action plan to make smarter marketing decisions.
          </p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.8 }} className="max-w-[720px] mx-auto">
          <div className="relative bg-[#0A0A0F] border-2 border-[#D4FF4A]/45 rounded-2xl p-8 lg:p-10 shadow-[0_60px_120px_-60px_rgba(212,255,74,0.28)]">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 px-3 py-1 rounded-full bg-[#D4FF4A] text-[#030305] text-[10px] font-mono font-semibold tracking-[0.14em] uppercase whitespace-nowrap">
              Only five spots
            </div>
            <div className="text-center mb-8 pb-8 border-b border-white/[0.06]">
              <div className="font-mono-label text-[#71717A] text-[10px] mb-3">ICP Intel Growth Intelligence Beta</div>
              <div className="flex items-baseline justify-center gap-3 flex-wrap">
                <span className="font-display text-white text-[64px] lg:text-[78px] font-bold leading-none tracking-[-0.03em]" data-testid="pricing-total">$999</span>
                <span className="font-mono text-[#A1A1AA] text-[13px]">one time</span>
              </div>
              <p className="mt-4 text-[#A1A1AA] text-[13px]">No subscription. No ongoing retainer.</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-7 gap-y-3 mb-8">
              {features.map((feature) => <FeatureRow key={feature}>{feature}</FeatureRow>)}
            </div>

            <a href="https://tidycal.com/nick-christensen/30-minute-meeting" onClick={() => trackBetaApplication("pricing")} target="_blank" rel="noopener noreferrer" className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 bg-[#D4FF4A] text-[#030305] text-[14px] font-semibold rounded-full hover:bg-white transition-colors" data-testid="pricing-cta">
              Apply for one of five beta spots
              <span aria-hidden>→</span>
            </a>
            <p className="mt-4 text-center font-mono text-[#71717A] text-[10px] leading-[1.6]">
              Delivered within 10 business days after we accept your data. Implementation and ongoing campaign management are not included.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function FeatureRow({ children }) {
  return (
    <div className="flex items-start gap-2.5">
      <svg width="14" height="14" viewBox="0 0 16 16" fill="none" className="mt-[3px] shrink-0">
        <path d="M3 8l3.5 3.5L13 5" stroke="#D4FF4A" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      <span className="text-[#D4D4D8] text-[13px] leading-[1.5]">{children}</span>
    </div>
  );
}
